import type { City } from '../api/types';

/*
  The city being worked on, across the screens.

  The single-day form, the operation form and the coverage page are three
  pages that hold entirely separate state — switching between them tears
  one down and builds the next. Each used to open on the configured
  default, so choosing a city and then switching screen put somebody in
  another city without saying so.

  Kept in the session rather than in a context: what has to survive is a
  remount rather than a render, and sessionStorage carries it across a
  reload of the tab while keeping it out of other tabs. Every read and
  write is guarded — a private window can refuse storage altogether, and
  the configured default is a perfectly good answer when it does.
*/
const CITY_KEY = 'travplaces.city';

/** Says which city is being worked on, for whatever screen comes next. */
export function rememberCity(city: City | null): void {
  if (!city) return;
  try {
    window.sessionStorage.setItem(CITY_KEY, city.id);
  } catch {
    /* Storage refused: the next screen opens on the configured default. */
  }
}

function recalledCityId(): string {
  try {
    return window.sessionStorage.getItem(CITY_KEY) ?? '';
  } catch {
    return '';
  }
}

/**
 * Which city a screen opens on.
 *
 * Whichever was last being worked on, so a change of screen is not a
 * change of city. Failing that the configured one, where an operation has
 * a home city and somebody has said so; failing that the first, because a
 * screen that opens on nothing asks a question before it has told the
 * reader anything.
 *
 * Shared rather than written out per page: two screens disagreeing about
 * where the work is would be a bug nobody could see from either of them.
 */
export function pickDefaultCity(
  cities: City[],
  settings: Record<string, unknown>,
): City | null {
  const recalled = cities.find((c) => c.id === recalledCityId());
  if (recalled) return recalled;

  const slug = settings['planner.default_city_slug'];

  return (typeof slug === 'string' && cities.find((c) => c.slug === slug))
    || cities[0]
    || null;
}
